import React, { useState } from "react";
import { HiOutlineChevronLeft, HiOutlineChevronRight, HiOutlinePlay } from "react-icons/hi2";
import VideoModal from "./VideoModal";

interface VideoCarouselProps {
  songsData: { youtubeLink: string; title: string; artist: string }[];
}

function withEmbedPlaybackParams(link: string, autoplay: boolean) {
  try {
    const url = new URL(link);
    url.searchParams.set("playsinline", "1");
    url.searchParams.set("rel", "0");
    if (autoplay) {
      url.searchParams.set("autoplay", "1");
      url.searchParams.set("mute", "0");
    }
    return url.toString();
  } catch {
    return link;
  }
}

const iframeAllow =
  "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen";

const VideoCarousel: React.FC<VideoCarouselProps> = ({ songsData }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const previousVideo = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? songsData.length - 1 : prevIndex - 1
    );
  };

  const nextVideo = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
    setCurrentIndex((prevIndex) =>
      prevIndex === songsData.length - 1 ? 0 : prevIndex + 1
    );
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  if (songsData.length === 0) return null;

  const current = songsData[currentIndex];

  return (
    <div className="relative w-full">
      <div className="space-y-4">
        <div
          className="relative aspect-video w-full overflow-hidden rounded-xl bg-gray-900 cursor-pointer group touch-manipulation"
          onClick={openModal}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              openModal();
            }
          }}
          aria-label={`Play ${current.title}`}
        >
          {/* Preview iframe — non-interactive; tap opens fullscreen modal */}
          <iframe
            src={withEmbedPlaybackParams(current.youtubeLink, false)}
            title={`YouTube video ${currentIndex + 1}`}
            frameBorder="0"
            allow={iframeAllow}
            allowFullScreen
            className="absolute inset-0 w-full h-full pointer-events-none"
          />

          {/* Always-visible play control (hover-only was invisible on mobile) */}
          <div className="absolute inset-0 flex items-center justify-center bg-black/25 group-hover:bg-black/35 transition-colors duration-200">
            <div className="w-16 h-16 rounded-full bg-white/95 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-200">
              <HiOutlinePlay className="w-8 h-8 text-[#5932EA] ml-1" strokeWidth={2} />
            </div>
          </div>

          <button
            onClick={previousVideo}
            type="button"
            className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 backdrop-blur-sm shadow-lg flex items-center justify-center text-gray-800 hover:bg-white hover:scale-110 transition-all duration-200 touch-manipulation"
            aria-label="Previous video"
          >
            <HiOutlineChevronLeft className="w-5 h-5" strokeWidth={2} />
          </button>
          <button
            onClick={nextVideo}
            type="button"
            className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 backdrop-blur-sm shadow-lg flex items-center justify-center text-gray-800 hover:bg-white hover:scale-110 transition-all duration-200 touch-manipulation"
            aria-label="Next video"
          >
            <HiOutlineChevronRight className="w-5 h-5" strokeWidth={2} />
          </button>

          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 pointer-events-none">
            {songsData.map((_, i) => (
              <span
                key={i}
                className={`w-1.5 h-1.5 rounded-full transition-colors ${
                  i === currentIndex ? "bg-white" : "bg-white/40"
                }`}
              />
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-0.5">
          <h3 className="font-semibold text-gray-800 dark:text-gray-100 line-clamp-2">
            {current.title}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">{current.artist}</p>
        </div>
      </div>

      {isModalOpen && (
        <VideoModal onClose={closeModal}>
          <div className="aspect-video w-full max-w-4xl">
            <iframe
              src={withEmbedPlaybackParams(current.youtubeLink, true)}
              title={`YouTube video ${currentIndex + 1}`}
              frameBorder="0"
              allow={iframeAllow}
              allowFullScreen
              className="w-full h-full rounded-xl"
            />
          </div>
        </VideoModal>
      )}
    </div>
  );
};

export default VideoCarousel;
